import Dexie, { type Table } from 'dexie';
import type {
  Favourite, Note, StudySession, ReadingPlanProgress,
  UserPreferences, ActiveSessionMarker, MeetingsFlag,
} from '@/types/user';
import type { TagId } from '@/types/tags';
import { DEFAULT_PREFERENCES } from '@/types/user';

export interface UserVerseTag {
  /** "BOOK:chapter:verse" — primary key */
  ref: string;
  tags: TagId[];
  updatedAt: number;
}

export class JndDb extends Dexie {
  favourites!: Table<Favourite, string>;
  notes!: Table<Note, string>;
  sessions!: Table<StudySession, string>;
  plans!: Table<ReadingPlanProgress, string>;
  prefs!: Table<UserPreferences, 'singleton'>;
  active!: Table<ActiveSessionMarker, 'active'>;
  meetingsFlag!: Table<MeetingsFlag, 'singleton'>;
  userTags!: Table<UserVerseTag, string>;

  constructor() {
    super('jnd-bible');
    this.version(1).stores({
      favourites: 'id, createdAt',
      notes: 'id, updatedAt',
      sessions: 'id, updatedAt, closedAt',
      plans: 'planId, lastReadAt',
      prefs: 'id',
      active: 'id',
      meetingsFlag: 'id',
    });
    this.version(2).stores({
      favourites: 'id, createdAt',
      notes: 'id, updatedAt',
      sessions: 'id, updatedAt, closedAt',
      plans: 'planId, lastReadAt',
      prefs: 'id',
      active: 'id',
      meetingsFlag: 'id',
      userTags: 'ref, updatedAt',
    });
  }
}

export const db = new JndDb();

export async function ensureDefaults(): Promise<UserPreferences> {
  const existing = await db.prefs.get('singleton');
  if (existing) return existing;
  await db.prefs.put({ ...DEFAULT_PREFERENCES });
  return DEFAULT_PREFERENCES;
}

export async function getActiveSessionId(): Promise<string | null> {
  const row = await db.active.get('active');
  return row?.sessionId ?? null;
}

export async function setActiveSessionId(id: string | null): Promise<void> {
  await db.active.put({ id: 'active', sessionId: id });
}

export function newId(): string {
  // 22-char base64url id
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  let bin = '';
  for (const b of arr) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
